import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articleTypesApi, type ArticleTypeDetail, type ArticleTypeRequest } from "../api/articles";
import { attributesApi, type AttributeView } from "../api/catalog";
import type { ApiError } from "../api/client";
import { detailsMap, formatApiError } from "../lib/apiError";
import { useAuth } from "../auth/AuthContext";
import { codebookWrite } from "../lib/codebookFunctions";
import { useToast } from "../components/Toast";
import { usePrompt } from "../components/ConfirmDialog";
import {
  AttributeDefsEditor,
  draftFromAttribute,
  draftsFromDefs,
  inlineDefsFromDrafts,
  type AttributeDefDraft,
} from "../components/AttributeDefsEditor";
import { AttributeFormModal } from "../components/AttributeFormModal";
import { HistoryPanel } from "../components/HistoryPanel";
import { PermLink } from "../components/PermLink";
import Ic from "../components/icons.jsx";

type BaseForm = { code: string; name: string; description: string };

const EMPTY_FORM: BaseForm = { code: "", name: "", description: "" };

/**
 * `/tipovi/artikli/novi` and `/tipovi/artikli/:id` (sprint 5l §2, §7). An article type
 * ("Ležaj", "Šraf", "Filter vreća") says which data every article of that type carries: base
 * fields plus attributes picked from the attribute codebook, saved together (POST / PUT with
 * `attributeDefs`) — the same editor as a part type, without attachments and notes (v1).
 */
export default function ArticleTypeEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const prompt = usePrompt();
  const { hasPermission } = useAuth();
  const canManage = isNew ? hasPermission("article-types:create") : hasPermission("article-types:edit");
  const canDelete = hasPermission("article-types:delete");
  const canCreateAttribute = canManage && hasPermission("attributes:create");
  // Same rule as the codebook's route and menu item: any of its three functions opens it.
  const canOpenCodebook = codebookWrite("attributes").some((p) => hasPermission(p));

  const [detail, setDetail] = useState<ArticleTypeDetail | null>(null);
  const [form, setForm] = useState<BaseForm>(EMPTY_FORM);
  const [drafts, setDrafts] = useState<AttributeDefDraft[]>([]);
  const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
  const [attributes, setAttributes] = useState<AttributeView[]>([]);
  const [attributeForm, setAttributeForm] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const applyDetail = useCallback((d: ArticleTypeDetail) => {
    setDetail(d);
    setForm({ code: d.code, name: d.name, description: d.description ?? "" });
    setDrafts(draftsFromDefs(d.attributeDefs));
    setDraftErrors({});
  }, []);

  const load = useCallback(async (typeId: string) => {
    setLoadError(null);
    try {
      applyDetail(await articleTypesApi.get(typeId));
    } catch (err) {
      const ae = err as ApiError;
      setLoadError(ae.status === 404 ? t("articleTypes.notFound", "Tip artikla ne postoji.") : ae.message);
    }
  }, [applyDetail, t]);

  const loadAttributes = useCallback(async () => {
    try {
      setAttributes(await attributesApi.list());
    } catch (err) {
      setLoadError((err as ApiError).message);
    }
  }, []);

  useEffect(() => {
    void loadAttributes();
  }, [loadAttributes]);

  useEffect(() => {
    if (id) void load(id);
    else { setDetail(null); setForm(EMPTY_FORM); setDrafts([]); setDraftErrors({}); }
  }, [id, load]);

  function set<K extends keyof BaseForm>(key: K, value: BaseForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  /** A missing attribute created from here goes into the codebook and straight onto this type. */
  async function handleAttributeCreated(created: AttributeView) {
    setAttributeForm(false);
    setAttributes((list) => [...list, created].sort((a, b) => a.label.localeCompare(b.label, "sr-Latn")));
    setDrafts((list) => [...list, draftFromAttribute(created)]);
    setDraftErrors({});
    toast.success(t("attributes.toast.created", "Atribut dodat u šifarnik."));
  }

  /** Server-side `details.attributeDefs[i].attributeId` → the i-th row's error. */
  function applyServerDefErrors(ae: ApiError): boolean {
    const details = detailsMap(ae);
    if (ae.code !== "VALIDATION" || !details) return false;
    const rowErrors: Record<string, string> = {};
    for (const [field, message] of Object.entries(details)) {
      const m = /^attributeDefs\[(\d+)\]/.exec(field);
      const row = m ? drafts[Number(m[1])] : undefined;
      if (row) {
        rowErrors[row.rowId] = message === "not found"
          ? t("attributeDefs.error.notInCodebook", "Atribut više ne postoji u šifarniku — uklonite ga sa tipa.")
          : message;
      }
    }
    if (!Object.keys(rowErrors).length) return false;
    setDraftErrors(rowErrors);
    setError(t("attributeDefs.error.fixRows", "Ispravite označene atribute."));
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setDraftErrors({});
    const body: ArticleTypeRequest = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      attributeDefs: inlineDefsFromDrafts(drafts),
    };
    setSubmitting(true);
    try {
      if (isNew) {
        const created = await articleTypesApi.create(body);
        toast.success(t("articleTypes.toast.created", "Tip artikla kreiran."));
        navigate(`/tipovi/artikli/${created.id}`, { replace: true });
      } else if (id) {
        applyDetail(await articleTypesApi.update(id, body));
        toast.success(t("articleTypes.toast.saved", "Tip artikla sačuvan."));
      }
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "CODE_TAKEN") setError(t("articleTypes.error.codeTaken", "Šifra je već zauzeta."));
      else if (!applyServerDefErrors(ae)) setError(formatApiError(ae));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!detail) return;
    const reason = await prompt({
      title: t("articleTypes.confirmDelete.title", "Brisanje tipa artikla"),
      message: t("articleTypes.confirmDelete.message", "Obrisati tip artikla „{{name}}“ ({{code}})?", { name: detail.name, code: detail.code }),
      confirmLabel: t("action.delete", "Obriši"),
      placeholder: t("deleteReason.placeholder", "Razlog brisanja (obavezno) — npr. rashodovano, zamenjeno, greška pri unosu…"),
      multiline: true,
      required: true,
      danger: true,
    });
    if (!reason) return;
    try {
      await articleTypesApi.remove(detail.id, reason);
      toast.success(t("articleTypes.toast.deleted", "Tip artikla obrisan."));
      navigate("/tipovi/artikli", { replace: true });
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "IN_USE") {
        const count = Number(detailsMap(ae)?.articleCount ?? detail.articleCount);
        toast.error(t("articleTypes.error.inUse", "Tip ima {{count}} artikala i ne može se obrisati — prvo obrišite ili prebacite te artikle.", { count }));
      } else {
        toast.error(ae.message);
      }
    }
  }

  const readOnly = !canManage;
  const title = isNew
    ? t("articleTypes.editor.newTitle", "Nov tip artikla")
    : detail?.name ?? t("articleTypes.editor.title", "Tip artikla");
  // The type cannot go while it has articles — the button says so instead of waiting for the 409.
  const deleteReason = detail && detail.articleCount > 0
    ? t("articleTypes.error.inUse", "Tip ima {{count}} artikala i ne može se obrisati — prvo obrišite ili prebacite te artikle.", { count: detail.articleCount })
    : null;

  return (
    <div>
      <div className="page-head">
        <div>
          <nav className="crumbs" aria-label="Breadcrumb">
            <span><Link to="/tipovi/artikli">{t("articleTypes.title", "Tipovi artikala")}</Link></span>
            <span>{isNew ? t("articleTypes.editor.newCrumb", "Novi") : detail?.code ?? "…"}</span>
          </nav>
          <h1>{title}</h1>
          {!isNew && detail && (
            <div className="sub">
              {t("articleTypes.editor.attrCount", "{{count}} atributa", { count: detail.attributeDefs.length })}
              {" · "}
              <PermLink permission="articles:view" to={`/artikli?typeId=${detail.id}`}>
                {t("articleTypes.editor.articleCount", "{{count}} artikala", { count: detail.articleCount })}
              </PermLink>
            </div>
          )}
        </div>
        {canDelete && !isNew && detail && (
          <div className="page-actions">
            <span className="tip-wrap" data-tip={deleteReason ?? undefined} title={deleteReason ?? undefined}>
              <button type="button" className="btn" style={dangerBtn} disabled={deleteReason !== null} onClick={handleDelete}>
                {t("articleTypes.deleteType", "Obriši tip artikla")}
              </button>
            </span>
          </div>
        )}
      </div>

      {loadError && <div style={errorBox} role="alert">{loadError}</div>}
      {!isNew && !detail && !loadError && <div className="card"><div className="empty">{t("common.loading", "Učitavanje…")}</div></div>}

      {(isNew || detail) && (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Base fields */}
          <section className="card">
            <div className="card-h"><h3>{t("articleTypes.editor.baseSection", "Osnovni podaci")}</h3></div>
            <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div className="form-grid">
                <label className="field">
                  <span>{t("articleTypes.field.code", "Šifra")} <span className="req-mark">*</span></span>
                  {/* Codes are upper-case (2026-10-12): the field capitalises while typing, the server does the same on save. */}
                  <input className="input" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} required maxLength={40} disabled={readOnly} placeholder="LEZAJ" autoCapitalize="characters" style={{ fontFamily: "var(--mono)" }} title={readOnly ? t("articleTypes.editor.readOnly", "Nemate pravo izmene tipova artikala.") : undefined} />
                </label>
                <label className="field">
                  <span>{t("articleTypes.field.name", "Naziv")} <span className="req-mark">*</span></span>
                  <input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} required maxLength={200} disabled={readOnly} placeholder="Ležaj" title={readOnly ? t("articleTypes.editor.readOnly", "Nemate pravo izmene tipova artikala.") : undefined} />
                </label>
              </div>
              <label className="field">
                <span>{t("articleTypes.field.description", "Opis")}</span>
                <textarea className="input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} disabled={readOnly} maxLength={4000} style={textarea} title={readOnly ? t("articleTypes.editor.readOnly", "Nemate pravo izmene tipova artikala.") : undefined} />
              </label>
            </div>
          </section>

          {/* Attributes picked from the codebook, saved with the type (sprint 5f pattern) */}
          <section className="card">
            <div className="card-h">
              <div>
                <h3>{t("articleTypes.editor.attrsSection", "Atributi")}</h3>
                <div className="sub">{t("articleTypes.editor.attrsSub", "Podaci koje nosi svaki artikal ovog tipa (navoj, dužina, prečnik…). Atributi se biraju iz šifarnika; ovde se određuje samo da li je atribut obavezan.")}</div>
              </div>
              {canOpenCodebook && (
                <div style={{ flex: "none" }}>
                  <Link className="btn sm" to="/admin/atributi">{t("attributes.title", "Šifarnik atributa")}</Link>
                </div>
              )}
            </div>
            <AttributeDefsEditor
              drafts={drafts}
              onChange={(next) => { setDrafts(next); setDraftErrors({}); }}
              attributes={attributes}
              disabled={readOnly}
              errors={draftErrors}
              footerExtra={canCreateAttribute && (
                <button type="button" className="btn sm" onClick={() => setAttributeForm(true)}>
                  <Ic.plus /> {t("attributes.new", "Novi atribut")}
                </button>
              )}
            />
          </section>

          {error && <div style={errorBox} role="alert">{error}</div>}

          {canManage && (
            <div className="form-actions">
              <button type="button" className="btn" onClick={() => navigate("/tipovi/artikli")}>{isNew ? t("action.cancel", "Otkaži") : t("action.back", "Nazad")}</button>
              <button type="submit" className="btn primary" disabled={submitting} title={submitting ? t("action.saving", "Čuvanje…") : undefined}>
                {submitting ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
              </button>
            </div>
          )}
        </form>
      )}

      {/* Outside the <form>: the attribute modal has its own form (no nesting). */}
      {attributeForm && (
        <AttributeFormModal attribute={null} onClose={() => setAttributeForm(false)} onSaved={handleAttributeCreated} />
      )}

      {!isNew && detail && (
        <div style={{ marginTop: 16 }}>
          <HistoryPanel entityKind="ARTICLE_TYPE" entityId={detail.id} />
        </div>
      )}
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
